import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { tokenHitFragment } from '@sorare/core/src/lib/algolia';

import { BuildingPage } from './BuildingPage/index.tsx';
import { Summary } from './Summary/index.tsx';
import type {
  NewOfferBuilder_anyCard,
  TradeCardsSearchQuery,
  TradeCardsSearchQueryVariables,
} from './__generated__/searchQuery.graphql.ts';
import { useOnSubmit } from './useOnSubmit.ts';

export const anyCardFragment = gql`
  fragment NewOfferBuilder_anyCard on AnyCardInterface {
    slug
    ...useOnSubmit_anyCard
    ...OfferBuilderBuildingPage_anyCard
    ...OfferBuilderSummary_anyCard
  }
  ${useOnSubmit.fragments.anyCard}
  ${Summary.fragments.anyCard}
  ${BuildingPage.fragments.anyCard}
` as TypedDocumentNode<NewOfferBuilder_anyCard>;

/**
 * The picker's own search: the hits carry the card fields the builder renders, so
 * nothing re-fetches them. `NEW_OFFER_CARDS_QUERY` stays for the Algolia picker,
 * whose hits are not GraphQL cards.
 */
export const TRADE_CARDS_SEARCH_QUERY = gql`
  query TradeCardsSearchQuery(
    $userSlug: String!
    $sport: Sport!
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $onlyPrimary: Boolean = false
  ) {
    user(slug: $userSlug) {
      slug
      searchCards(
        query: $query
        page: $page
        pageSize: $pageSize
        sorts: $sorts
        sport: $sport
        advancedFilters: $advancedFilters
        refinements: $refinements
        facets: $facets
      ) {
        page
        nbPages
        nbHits
        hits {
          slug
          ...Algolia_CardHit_anyCard
          ...NewOfferBuilder_anyCard
        }
        facets {
          field
          values {
            value
            count
          }
          min {
            value
          }
          max {
            value
          }
        }
      }
    }
  }
  ${tokenHitFragment}
  ${anyCardFragment}
` as TypedDocumentNode<TradeCardsSearchQuery, TradeCardsSearchQueryVariables>;
